import { useMutation } from '@tanstack/react-query';
import { Form, Modal, ModalProps } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { CustomerApis } from 'apis/CustomerApis';
import { SalesInfoApis } from 'apis/SalesInfoApis';
import { Button, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { IGetOrderTempByIdParams } from 'interfaces/SalesInfo';
import { toastError } from 'utils/constants/functions';
import { useUser } from 'contexts/UserProvider';

type IMergeOrderModalProps = {
  onOk(formValues: any): void;
} & Omit<ModalProps, 'onOk'>;

export const MergeOrderModal = memo(
  ({ open, loading, onCancel, onOk }: IMergeOrderModalProps) => {
    const { user } = useUser();

    const [form] = Form.useForm();

    const [customer, setCustomer] = useState(initLazySelectData),
      [orderTypeOptions, setOrderTypeOptions] = useState(
        [] as DefaultOptionType[],
      ),
      [order, setOrder] = useState(initLazySelectData),
      [getOrderParams, setGetOrderParams] = useState({
        customerId: undefined,
        orderTypeId: undefined,
        orderIds: [],
      } as { customerId?: string; orderTypeId?: string; orderIds: string[] });

    const {
        mutate: getCustomersInComboboxMutate,
        isPending: isGetCustomersInComboboxMutatePending,
      } = useMutation({
        mutationFn: (params: { pageSize: number; searchValue?: string }) =>
          CustomerApis.getCustomersInCombobox(params),
        onSuccess: (res) => {
          if (res?.data) {
            const data = res?.data?.getCustomer;

            setCustomer({
              totalItems: data?.pageInfo?.totalItems ?? 0,
              options: (data?.items ?? []).map(
                (item: any) =>
                  ({
                    label: `${item?.sapCustomerId} - ${item?.storeName}`,
                    value: item?.id,
                  }) as DefaultOptionType,
              ),
            });
          }
        },
      }),
      {
        mutate: getOrderStatusComboBoxMutate,
        isPending: isGetOrderStatusComboBoxMutatePending,
      } = useMutation({
        mutationFn: () =>
          SalesInfoApis.getOrderStatusComboBox({ groupId: '109' }),
        onSuccess: (res) => {
          if (res?.data)
            setOrderTypeOptions(
              (res?.data?.getCode?.items ?? []).map(
                (item: any) =>
                  ({
                    label: item?.name,
                    value: item?.id,
                  }) as DefaultOptionType,
              ),
            );
        },
      }),
      {
        mutate: getOrderTempByIdMutate,
        isPending: isGetOrderTempByIdMutatePending,
      } = useMutation({
        mutationFn: (params: IGetOrderTempByIdParams) =>
          SalesInfoApis.getOrderTempById(params),
        onSuccess: (res: any) => {
          if (res?.data?.getOrderNotMainOrderWeb) {
            const data = res?.data?.getOrderNotMainOrderWeb;

            setOrder({
              totalItems: data?.pageInfo?.totalItems,
              options: (data?.items ?? []).map(
                (item: any) =>
                  ({
                    label: `${item?.id} - ${item?.tteamSale?.subId}`,
                    value: item?.id,
                  }) as DefaultOptionType,
              ),
            });

            return;
          }

          if ((res?.errors ?? []).length >= 0)
            toastError(
              `Lỗi khi lấy danh sách đơn hàng để gộp. ${res?.errors?.[0]?.message}`,
            );
        },
        onError: (error) => {
          toastError(
            `Lỗi khi lấy danh sách đơn hàng để gộp. ${error?.message ?? error}`,
          );
        },
      });

    useEffect(() => {
      if (!open) {
        setGetOrderParams({
          customerId: undefined,
          orderTypeId: undefined,
          orderIds: [],
        });

        return;
      }

      getOrderStatusComboBoxMutate();
    }, [open]);

    useEffect(() => {
      form.setFieldsValue({
        customerId: getOrderParams.customerId,
        orderTypeId: getOrderParams.orderTypeId,
        orderIds: getOrderParams.orderIds,
      });

      if (!getOrderParams?.customerId || !getOrderParams?.orderTypeId) {
        setOrder({ totalItems: 0, options: [] });

        return;
      }

      getOrderTempByIdMutate({
        customerId: getOrderParams.customerId,
        orderTypeId: getOrderParams.orderTypeId,
        pageSize: 10,
        plantId: user?.plantCode,
      });
    }, [getOrderParams]);

    const onCustomerScrollChange = useCallback(
      (params: ILazySelectParams) => {
        if (!open) return;

        const { pageSize, value } = params;

        getCustomersInComboboxMutate({ pageSize, searchValue: value?.trim() });
      },
      [open],
    );

    const onOrderScrollChange = useCallback(
      (params: ILazySelectParams) => {
        if (!open) return;

        if (!getOrderParams?.customerId || !getOrderParams?.orderTypeId) return;

        const { pageSize, value } = params;

        getOrderTempByIdMutate({
          pageSize,
          searchValue: value?.trim(),
          plantId: user?.plantCode,
          customerId: getOrderParams.customerId,
          orderTypeId: getOrderParams.orderTypeId,
        });
      },
      [open, getOrderParams],
    );

    const onCustomerSelect = useCallback(
      (value: string) => {
        setGetOrderParams({
          ...getOrderParams,
          customerId: value,
          orderIds: [],
        });
      },
      [getOrderParams],
    );

    const onOrderTypeSelect = useCallback(
      (value: string) => {
        setGetOrderParams({
          ...getOrderParams,
          orderTypeId: value,
          orderIds: [],
        });
      },
      [getOrderParams],
    );

    const onCustomerClearSelect = useCallback(() => {
      setGetOrderParams({
        ...getOrderParams,
        customerId: undefined,
        orderIds: [],
      });
    }, [getOrderParams]);

    const onOrderTypeClearSelect = useCallback(() => {
      setGetOrderParams({
        ...getOrderParams,
        orderTypeId: undefined,
        orderIds: [],
      });
    }, [getOrderParams]);

    const onOrderSelect = useCallback(
      (value: string) => {
        setGetOrderParams({
          ...getOrderParams,
          orderIds: [...getOrderParams.orderIds, value],
        });
      },
      [getOrderParams],
    );

    const onOrderDeSelect = useCallback(
      (value: string) => {
        setGetOrderParams({
          ...getOrderParams,
          orderIds: getOrderParams.orderIds.filter((order) => order !== value),
        });
      },
      [getOrderParams],
    );

    const handleMergeOrder = useCallback(() => {
      onOk(form.getFieldsValue());
    }, [onOk]);

    const isOkMergeDisabled = useMemo(() => {
      return (
        loading ||
        !getOrderParams.customerId ||
        !getOrderParams.orderTypeId ||
        getOrderParams.orderIds.length < 1
      );
    }, [loading, getOrderParams]);

    return (
      <Modal
        title="Gộp đơn"
        open={open}
        className="min-w-[600px]"
        onCancel={onCancel}
        footer={
          <div className="flex justify-end mt-4 gap-2">
            <Button
              title="Hủy"
              customClass="!border-transparent !text-neutral-30 !bg-transparent"
              disabled={loading}
              handleOnclick={onCancel}
            />
            <Button
              fill
              title="Tạo đơn"
              disabled={isOkMergeDisabled}
              handleOnclick={handleMergeOrder}
            />
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="customerId"
            rules={[{ required: true, message: 'Chọn khách hàng' }]}
          >
            <LazySelectForm
              data={customer}
              disabled={loading}
              placeholder="Chọn Khách hàng"
              loading={isGetCustomersInComboboxMutatePending}
              onSelect={onCustomerSelect}
              onClear={onCustomerClearSelect}
              onSelectScrollChange={onCustomerScrollChange}
            />
          </Form.Item>
          <Form.Item
            name="orderTypeId"
            rules={[{ required: true, message: 'Chọn loại đơn' }]}
          >
            <SelectForm
              options={orderTypeOptions}
              disabled={loading}
              placeholder="Chọn loại đơn"
              loading={isGetOrderStatusComboBoxMutatePending}
              onSelect={onOrderTypeSelect}
              onClear={onOrderTypeClearSelect}
            />
          </Form.Item>
          <Form.Item
            name="orderIds"
            // rules={[{ required: true, message: 'Chọn bảng kê bán hàng - Đội' }]}
          >
            <LazySelectForm
              data={order}
              disabled={loading}
              mode="multiple"
              placeholder="Chọn bảng kê bán hàng - Đội"
              loading={isGetOrderTempByIdMutatePending}
              onSelect={onOrderSelect}
              onDeselect={onOrderDeSelect}
              onSelectScrollChange={onOrderScrollChange}
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  },
);
